import { type Table } from "@tanstack/react-table"
import { X } from "lucide-react"
import { useRef, useEffect, useCallback } from "react"

import { Button } from "../button"
import { Input } from "../input"
import { DataGridViewOptions } from "./data-grid-view-options"
import { DataGridDensityToggle, type RowDensity } from "./data-grid-density-toggle"

interface DataGridToolbarProps<TData> {
    table: Table<TData>
    searchColumn?: string
    searchPlaceholder?: string
    children?: React.ReactNode
    /** Additional content to show only when faceted filters are visible */
    facetedFilters?: React.ReactNode
    /** When true, hides the faceted filters (e.g., when advanced filters are active) */
    hideFacetedFilters?: boolean
    /** When true, shows a global reset button for all filters (default: false) */
    showResetButton?: boolean
    density?: RowDensity
    onDensityChange?: (density: RowDensity) => void
    /** Column order state for reordering */
    columnOrder?: string[]
    onColumnOrderChange?: (order: string[]) => void
    /** Additional CSS classes for the toolbar's inner content container */
    contentClassName?: string
}

// Helper to extract search value - handles both string and object formats
function getSearchValue(filterValue: unknown): string {
    if (filterValue === null || filterValue === undefined) return "";
    if (typeof filterValue === "string") return filterValue;
    // Handle advanced filter format: { operator: 'contains', value: 'search' }
    if (typeof filterValue === "object" && "value" in filterValue) {
        const val = (filterValue as { value?: unknown }).value;
        return typeof val === "string" ? val : "";
    }
    return "";
}

export function DataGridToolbar<TData>({
    table,
    searchColumn = "email",
    searchPlaceholder = "Filter...",
    children,
    facetedFilters,
    hideFacetedFilters = false,
    showResetButton = false, // Default to false - each filter has its own clear
    density,
    onDensityChange,
    columnOrder,
    onColumnOrderChange,
    contentClassName,
}: DataGridToolbarProps<TData>) {
    const isFiltered = table.getState().columnFilters.length > 0
    const searchColumnInstance = searchColumn ? table.getColumn(searchColumn) : undefined

    // Get the search value, handling both string and object formats
    const searchValue = getSearchValue(searchColumnInstance?.getFilterValue())

    // Ref to track search input and focus state
    const searchInputRef = useRef<HTMLInputElement>(null)
    const wasSearchFocusedRef = useRef(false)
    const cursorPositionRef = useRef<number>(0)
    const cursorEndRef = useRef<number>(0)

    // Track focus state before render
    useEffect(() => {
        const input = searchInputRef.current
        if (input && document.activeElement === input) {
            wasSearchFocusedRef.current = true
            cursorPositionRef.current = input.selectionStart || 0
            cursorEndRef.current = input.selectionEnd || 0
        }
    })

    // Restore focus after data updates if search was focused
    useEffect(() => {
        if (wasSearchFocusedRef.current && searchInputRef.current) {
            const input = searchInputRef.current
            // Use requestAnimationFrame to ensure DOM is updated
            requestAnimationFrame(() => {
                input.focus()
                // Restore cursor position and selection range
                const start = Math.min(cursorPositionRef.current, input.value.length)
                const end = Math.min(cursorEndRef.current, input.value.length)
                input.setSelectionRange(start, end)
            })
        }
    })

    // Handle input change while tracking cursor position
    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        cursorPositionRef.current = event.target.selectionStart || 0
        cursorEndRef.current = event.target.selectionEnd || 0
        wasSearchFocusedRef.current = true
        searchColumnInstance?.setFilterValue(event.target.value || undefined)
    }, [searchColumnInstance])

    return (
        <div className={`flex justify-between gap-2 p-1 ${contentClassName?.includes('flex-wrap') ? 'items-start' : 'items-center'}`}>
            <div className={`flex flex-1 items-center gap-2 min-w-0${contentClassName ? ` ${contentClassName}` : ''}`}>
                {searchColumnInstance && (
                    <Input
                        ref={searchInputRef}
                        placeholder={searchPlaceholder}
                        value={searchValue}
                        onChange={handleSearchChange}
                        onFocus={() => { wasSearchFocusedRef.current = true }}
                        onBlur={() => { wasSearchFocusedRef.current = false }}
                        className="h-8 w-[150px] lg:w-[250px] flex-shrink-0"
                    />
                )}
                {/* Faceted filters - hidden when advanced filters are active */}
                {!hideFacetedFilters && facetedFilters}
                {children}
                {/* Global reset button - disabled by default since each filter has its own clear */}
                {showResetButton && isFiltered && (
                    <Button
                        variant="ghost"
                        onClick={() => table.resetColumnFilters()}
                        className="h-8 px-2 lg:px-3 flex-shrink-0"
                    >
                        Reset
                        <X className="ml-2 h-4 w-4" />
                    </Button>
                )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                <DataGridViewOptions
                    table={table}
                    columnOrder={columnOrder}
                    onColumnOrderChange={onColumnOrderChange}
                />
                {density && onDensityChange && (
                    <DataGridDensityToggle
                        density={density}
                        onDensityChange={onDensityChange}
                    />
                )}
            </div>
        </div>
    )
}
